/**
 * Certificate of Origin for sales orders shipping to Puerto Rico: the one-page form the shipper
 * signs and a Chamber of Commerce notarizes, generated as a PDF from the order and its shipment.
 * Server side: libs/services/src/order/certificate-of-origin.ts. Ship generates one automatically
 * (and prints it through RxPrint when mapped); from Shipping on, the warehouse can generate one
 * with the packages and weight it knows, and reprint the current copy.
 */
import { gql } from '@apollo/client';

export const CERTIFICATE_OF_ORIGIN = gql`
  query WarehouseCertificateOfOrigin($orderId: ID!, $companyId: ID!) {
    salesOrderCertificateOfOrigin(orderId: $orderId, companyId: $companyId) {
      orderId
      vendorSalesOrderNumber
      available
      unavailableReason
      carrierName
      shipDate
      consigneeName
      packageCount
      grossWeightLbs
      document {
        id
        humanFilename
        link
      }
    }
  }
`;

export const GENERATE_CERTIFICATE_OF_ORIGIN = gql`
  mutation WarehouseGenerateCertificateOfOrigin(
    $orderId: ID!
    $companyId: ID!
    $input: CertificateOfOriginInput
  ) {
    salesOrderCertificateOfOriginGenerate(orderId: $orderId, companyId: $companyId, input: $input) {
      id
      humanFilename
      link
    }
  }
`;

/** Shipping and every vendor status after it. Mirrors CERTIFICATE_OF_ORIGIN_STATUSES. */
export const CERTIFICATE_OF_ORIGIN_STATUSES = ['shipping', 'shipped', 'customer_pickup', 'completed'] as const;

/** Exact (international avoirdupois pound). */
export const KILOGRAMS_PER_POUND = 0.45359237;

/** Whether the order gets the certificate button. The server re-checks every rule. */
export const showsCertificateOfOrigin = ({
  shipsToPuertoRico,
  vendorStatus
}: {
  shipsToPuertoRico?: boolean | null;
  vendorStatus?: string | null;
}): boolean =>
  !!shipsToPuertoRico &&
  (CERTIFICATE_OF_ORIGIN_STATUSES as readonly string[]).includes(String(vendorStatus ?? ''));

/** Pounds → kilos to 2 decimals, as the certificate prints them. Null for no positive weight. */
export const poundsToKilograms = (pounds?: number | string | null): number | null => {
  if (pounds === null || pounds === undefined || String(pounds).trim() === '') return null;
  const n = Number(pounds);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * KILOGRAMS_PER_POUND * 100) / 100;
};

/** DD/MM/YYYY, as the certificate prints a YYYY-MM-DD date. */
export const formatCertificateDate = (isoDate?: string | null): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate ?? '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : isoDate ?? '';
};

/**
 * The warehouse's half of the form: packages and gross weight (pounds), as typed. Blank sends
 * null — the server then fills it from the order record. Returns the input or an operator message.
 */
export const certificateOfOriginWarehouseInput = (
  packageCount: string | number | null | undefined,
  grossWeightLbs: string | number | null | undefined
):
  | { ok: true; input: { packageCount: number | null; grossWeightLbs: number | null } }
  | { ok: false; message: string } => {
  const packagesText = String(packageCount ?? '').trim();
  const weightText = String(grossWeightLbs ?? '').trim();
  const packages = packagesText ? Number(packagesText) : null;
  const weight = weightText ? Number(weightText) : null;
  if (packages !== null && (!Number.isInteger(packages) || packages < 1 || packages > 99_999)) {
    return { ok: false, message: 'Number of packages must be a whole number of at least 1.' };
  }
  if (weight !== null && (!Number.isFinite(weight) || weight <= 0 || weight > 1_000_000)) {
    return { ok: false, message: 'Gross weight must be a positive number of pounds.' };
  }
  return { ok: true, input: { packageCount: packages, grossWeightLbs: weight } };
};
